import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";

// Animated count-up counter that triggers when scrolled into view.
// Parses a leading integer and preserves any suffix (e.g. "%", "+", "/7").
// Uses framer-motion's onViewportEnter (same mechanism as <Reveal/>).
export const Counter = ({ value, duration = 1600, className = "" }) => {
  const str = String(value);
  const match = str.match(/^([^\d]*)([\d,]+)(.*)$/);
  const prefix = match ? match[1] : "";
  const target = match ? parseInt(match[2].replace(/,/g, ""), 10) : 0;
  const suffix = match ? match[3] : str;

  const [display, setDisplay] = useState(0);
  const started = useRef(false);

  const start = () => {
    if (started.current || target === 0) return;
    started.current = true;
    let startTs = null;
    const step = (ts) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  return (
    <motion.span
      className={className}
      onViewportEnter={start}
      viewport={{ once: true, margin: "-60px" }}
    >
      {prefix}
      {display.toLocaleString("en-US")}
      {suffix}
    </motion.span>
  );
};

// Dynamic Lucide icon by name
export const Icon = ({ name, className = "" }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.6} aria-hidden="true" />;
};

export const Overline = ({ children, className = "" }) => (
  <span
    className={`text-xs font-bold uppercase tracking-[0.22em] text-brand-teal ${className}`}
  >
    {children}
  </span>
);

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({
  overline,
  title,
  intro,
  align = "left",
  className = "",
  titleClassName = "",
  introClassName = "",
}) => (
  <div
    className={`${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} ${className}`}
  >
    {overline && <Overline>{overline}</Overline>}
    <h2
      className={`mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy ${titleClassName}`}
    >
      {title}
    </h2>
    {intro && (
      <p className={`mt-4 text-base sm:text-lg text-slate-500 leading-relaxed ${introClassName}`}>
        {intro}
      </p>
    )}
  </div>
);
